

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BUDDY_TOUR, BUDDY_TOUR_EVENT, findBuddyTourTarget, firstBuddyTourIndex, nextVisibleBuddyTourIndex } from 'config/buddy/tour';
import BuddySpeech, { type AskSendEvent } from './BuddySpeech';
import BuddySvg from './BuddySvg';
import { BUDDY_CONFIG, BUDDY_INLINE_CONFIG, isBuddyHiddenPath } from 'config/buddy/config';
import { streamBuddyReply } from 'config/buddy/completion';
import { startBuddyConversation } from 'config/buddy/conversations';
import {
  ASK_SHORT_REPLY,
  buildBuddyPagePrompt,
  promptForIcon,
  regionalSpelling,
} from 'config/buddy/prompt';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { addressContext } from 'features/conversation-preferences/address-form';
import { useAddressForm } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import type { AddressForm } from 'features/conversation-preferences/services/conversation-preferences-service';
import {
  Eye,
  BuddyFace,
  bubbleSideFor,
  caretClientX,
  isConversationPath,
  isConversationView,
  lineForPath,
  useMediaQuery,
  usePathname,
} from 'config/buddy/utils';
import {
  BLINK_JUMP,
  BLINK_JUMP_CHANCE,
  BUDDY_ANCHORS,
  BUDDY_SLOTS,
  BUDDY_TOKENS,
  EMOTES,
  EYE_RY,
  EXPRESSIONS,
  FACES,
  HOVER_LEAVE_MS,
  IDLE_MS,
  LOOK_DAMP,
  LOOK_MAX_X,
  LOOK_MAX_Y,
  LOOK_PARALLAX,
  LOOK_RANGE,
  LOOK_SPRING,
  MOBILE_QUERY,
  SACCADE_MIN,
  STORAGE_KEY,
  TRAVEL_MS,
  WANDER_MAX_MS,
  WANDER_MIN_MS,
  WANDER_REACH,
} from 'config/buddy/constants';

export { BUDDY_CONFIG };
export { BUDDY_TOKENS, BUDDY_SLOTS, EXPRESSIONS, BUDDY_ANCHORS };
export { Eye, BuddyFace };

type BuddySettings = typeof BUDDY_CONFIG;
type BuddySettingKey = keyof BuddySettings;
type BuddyOverrides = Partial<BuddySettings>;
type BuddyAnchor = { id: string; expression?: string; theme?: string };
type BuddyBox = { top: number; left: number; width: number; height: number };
/** CSS custom properties that place him: --bx, --by, --bbubble, --bcover, --btour-top. */
type BuddyPosition = Record<string, string>;
type BuddyHandle = {
  react: (next: string, ms?: number) => void;
  celebrate: () => void;
  dismiss: () => void;
};

function tourHole(box: BuddyBox) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const x = box.left;
  const y = box.top;
  const w = box.width;
  const h = box.height;
  const r = Math.min(4, w / 2, h / 2);
  const outer = `M0 0H${width}V${height}H0Z`;
  const inner = `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
  return `path(evenodd, "${outer} ${inner}")`;
}

/** Composer card Buddy docks into, or null while the conversation is still loading. */
function composerDockCard() {
  const field = document.querySelector<HTMLElement>('.chat-mentions-input__input');
  if (!field) return null;
  let card: HTMLElement | null = field;
  while (card && card !== document.body) {
    if (typeof card.className === 'string' && card.className.includes('sm:rounded-[24px]')) break;
    card = card.parentElement;
  }
  if (!card || card === document.body) return null;
  if (card.getBoundingClientRect().height < 1) return null;
  return card;
}

/** Right edge of the open left sidebar: icon rail, chats or settings panel beside it, or phone sheet. 0 when none. */
function sidebarRightEdge() {
  let edge = 0;
  document.querySelectorAll('[data-sidebar="sidebar"], [data-testid="sidebar-chats-panel"], [data-testid="sidebar-settings-panel"]').forEach((node) => {
    const box = node.getBoundingClientRect();
    if (box.width > 40 && box.left < window.innerWidth / 2) edge = Math.max(edge, box.right);
  });
  return edge;
}

/** Per-use setting: the override wins, otherwise BUDDY_CONFIG, read live. */
function readSetting<K extends BuddySettingKey>(overrides: BuddyOverrides, key: K): BuddySettings[K] {
  return (key in overrides ? overrides[key] : BUDDY_CONFIG[key]) as BuddySettings[K];
}

export type BuddyProps = {
  /** Sit in the page flow instead of floating. Starts from BUDDY_INLINE_CONFIG. */
  inline?: boolean;
  /** Overrides for this Buddy only, e.g. { speak: true }. */
  config?: BuddyOverrides;
  /** Inline only: face size in px. */
  size?: number;
  /** Inline only: the floating Buddy keeps showing, e.g. for a small icon. */
  keepFloating?: boolean;
  /** Floating only: hide him on this page. A tour still shows. */
  hidden?: boolean;
  anchors?: BuddyAnchor[];
  dismissible?: boolean;
  onDismiss?: () => void;
};

type BuddyBodyProps = Omit<BuddyProps, 'config'> & {
  overrides?: BuddyOverrides;
  hasPlan?: boolean;
  /** How he addresses the user; formal when unknown. */
  addressForm?: AddressForm;
};

const BuddyBody = forwardRef<BuddyHandle, BuddyBodyProps>(function BuddyBody(
  {
    anchors = BUDDY_ANCHORS,
    dismissible = true,
    onDismiss,
    hidden = false,
    inline = false,
    size: inlineSize = 85,
    keepFloating = false,
    overrides = {},
    hasPlan = false,
    addressForm,
  },
  ref
) {
  /* Unique per Buddy: two on one page would share an id, and a hidden one breaks the other's halo. */
  const haloId = `klaxHalo${useId().replace(/:/g, '')}`;
  /* Stable, so effects don't re-run each render; still reads the latest overrides and BUDDY_CONFIG. */
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  const setting = useCallback(
    <K extends BuddySettingKey>(key: K) => readSetting(overridesRef.current, key),
    []
  );
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const signedIn = useAuthStore((state) => state.isAuthenticated);

  const readDismissedFromStorage = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const [dismissed, setDismissed] = useState(() => dismissible && readDismissedFromStorage());
  const [closing, setClosing] = useState(false);
  const [anchorId] = useState(anchors[0]?.id ?? null);
  const [expression, setExpression] = useState<string>('neutral');
  const [blinking, setBlinking] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [typed, setTyped] = useState('');
  const [reply, setReply] = useState('');
  const [speechOpen, setSpeechOpen] = useState(true);
  const [asking, setAsking] = useState(false);
  const [touring, setTouring] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourBox, setTourBox] = useState<BuddyBox | null>(null);
  const [askText, setAskText] = useState('');
  const askRef = useRef<HTMLTextAreaElement>(null);
  const [typing, setTyping] = useState(false);
  const [pos, setPos] = useState<BuddyPosition>({
    '--bx': '100px',
    '--by': '30vh',
    '--bbubble': '311px',
  });
  const [size, setSize] = useState(85);
  /* Float and breathe start at the clock's phase, so a Buddy that remounts (one loading
     screen after another) carries on instead of hopping. Matches klax-float / klax-breathe. */
  const [startedAt] = useState(() => performance.now());
  const [docked, setDocked] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [approaching, setApproaching] = useState(false);
  const [covered, setCovered] = useState(!inline);
  /* Hidden to the corner by the user. */
  const parked = dismissed;
  const parkedRef = useRef(parked);
  parkedRef.current = parked;
  const [bubbleSide, setBubbleSide] = useState<'left' | 'right'>('right');
  const [iconPrompt, setIconPrompt] = useState<ReturnType<typeof promptForIcon>>(null);
  const [holding, setHolding] = useState(false);
  const holdingRef = useRef(false);
  const askedRef = useRef('');
  const askedPathRef = useRef('');
  const askedIconRef = useRef(false);
  /* The line he last spoke for. An Ask answer changes askedRef, not this. */
  const spokenRef = useRef('');
  /* Page or icon line still waiting, or streaming before its first words. */
  const speakAbortRef = useRef<AbortController | null>(null);
  const askAbortRef = useRef<AbortController | null>(null);
  const touringRef = useRef(false);

  const overrideRef = useRef<string | null>(null);
  const busyRef = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const buddyRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(pos);
  const sizeRef = useRef(size);
  const dragRef = useRef({
    active: false,
    placed: false,
    moved: false,
    fromFace: false,
    x: 0,
    y: 0,
    ox: 0,
    oy: 0,
    downX: 0,
    downY: 0,
  });
  const arrivingRef = useRef(false);
  const arriveRef = useRef(0);
  const hoverRef = useRef<{ x: number; y: number } | null>(null);
  const placeRef = useRef<() => void>(() => undefined);
  posRef.current = pos;
  sizeRef.current = size;
  const lookLRef = useRef<SVGGElement>(null);
  const lookRRef = useRef<SVGGElement>(null);
  const lookRef = useRef({
    gx: 0, // current gaze target
    gy: 0,
    mx: 0, // last pointer position
    my: 0,
    has: false,
    lastMove: 0,
    eyes: [
      { x: 0, y: 0, vx: 0, vy: 0 },
      { x: 0, y: 0, vx: 0, vy: 0 },
    ],
    raf: 0,
    pending: 0,
    wander: 0,
    kick: null as (() => void) | null,
  });

  /* Read inside the rAF loop without re-subscribing the listener. */
  const expressionRef = useRef('neutral');
  const blinkingRef = useRef(false);
  expressionRef.current = expression;
  blinkingRef.current = blinking;
  busyRef.current = dragging || closing;
  /* The UI language and the user's form decide his words, so a switch re-asks the page. */
  const { t, i18n } = useTranslation();
  const language = i18n?.language || 'en-US';
  const context = addressContext(addressForm);
  const labels = {
    dismiss: t('BUDDY_LABEL_DISMISS', { defaultValue: 'Hide Buddy' }),
    region: t('BUDDY_LABEL_REGION', { defaultValue: 'Buddy, your Klax assistant', context }),
    ask: t('BUDDY_LABEL_ASK', { defaultValue: 'Ask Buddy' }),
    back: t('BUDDY_LABEL_BACK', { defaultValue: 'Back' }),
    send: t('BUDDY_LABEL_SEND', { defaultValue: 'Send' }),
  };

  const dismiss = useCallback(() => {
    if (closing || dismissed) return;
    /* Stop a line that has not started showing, and forget it, so the face click asks for it once. */
    if (speakAbortRef.current) {
      speakAbortRef.current.abort();
      speakAbortRef.current = null;
      askedRef.current = '';
      spokenRef.current = '';
      /* An icon line cut this way must not read as "left the icon", or the face click stays silent. */
      askedIconRef.current = false;
    }
    /* Drop an icon visit too: the icon effect stops while he is hidden, so its leave would never land. */
    holdingRef.current = false;
    setHolding(false);
    hoverRef.current = null;
    setIconPrompt(null);
    setSpeechOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    setDismissed(true);
    if (onDismiss) onDismiss();
  }, [closing, dismissed, onDismiss]);

  const meet = useCallback(() => {
    setClosing(false);
    setDismissed(false);
    setSpeechOpen(true);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }, []);

  /* --- current anchor -------------------------------------------------- */
  const anchor = useMemo(
    () => anchors.find((a) => a.id === anchorId) || anchors[0],
    [anchors, anchorId]
  );
  const theme = anchor?.theme === 'dark' ? 'dark' : 'light';

  const path = usePathname();
  /* Settle the route before swapping the speech line so quick nav does not thrash. */
  const [speechPath, setSpeechPath] = useState(path);
  useEffect(() => {
    if (path === speechPath) return undefined;
    const wait = window.setTimeout(() => {
      setSpeechPath(window.location.pathname);
    }, reduced ? 0 : setting('replyMs'));
    return () => window.clearTimeout(wait);
  }, [path, speechPath, reduced, setting]);
  const routeLine = lineForPath(speechPath);
  const spoken = iconPrompt || routeLine;
  /* The written line, in the UI language and form, is also the key point he paraphrases.
     The request is the re-ask key: same page, language and form ask once. */
  const localAnswer = t(spoken.lineKey, { defaultValue: spoken.defaultValue, context });
  const message = buildBuddyPagePrompt({
    topic: spoken.topic,
    line: localAnswer,
    language,
    addressForm,
  });
  /* The greeting is free, so it is always asked and shows the written answer only when the call
     fails, as after the daily limit. Ask needs a plan or top-up credits: its follow-ups are billed turns. */
  const useAi = spoken.useAi !== false;
  const canAsk = hasPlan && setting('ask');

  useEffect(() => {
    setAsking(false);
    setAskText('');
  }, [message]);

  useEffect(() => {
    if (!setting('tour')) return undefined;
    let cancelled = false;
    let timer = 0;
    let observer: MutationObserver | null = null;

    const beginTour = (first: number) => {
      if (cancelled || first < 0) return;
      setAsking(false);
      setAskText('');
      setDismissed(false);
      setSpeechOpen(true);
      setArriving(false);
      setDocked(false);
      setTourStep(first);
      setTouring(true);
    };

    const stopWaiting = () => {
      window.clearTimeout(timer);
      observer?.disconnect();
      observer = null;
    };

    const openTour = () => {
      stopWaiting();
      const first = firstBuddyTourIndex();
      if (first >= 0) {
        beginTour(first);
        return;
      }
      observer = new MutationObserver(() => {
        const ready = firstBuddyTourIndex();
        if (ready < 0) return;
        stopWaiting();
        beginTour(ready);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-buddy-tour-target'],
      });
      timer = window.setTimeout(() => {
        if (cancelled) return;
        stopWaiting();
        beginTour(nextVisibleBuddyTourIndex(0));
      }, 2000);
    };

    window.addEventListener(BUDDY_TOUR_EVENT, openTour);
    return () => {
      cancelled = true;
      stopWaiting();
      window.removeEventListener(BUDDY_TOUR_EVENT, openTour);
    };
  }, [setting]);

  useEffect(() => {
    if (asking && askRef.current) askRef.current.focus();
  }, [asking]);

  useEffect(() => {
    if (!asking) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAsking(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asking]);

  const sendAsk = (event: AskSendEvent) => {
    event.preventDefault();
    const text = askText.trim();
    if (!text || !hasPlan) return;
    askAbortRef.current?.abort();
    const controller = new AbortController();
    askAbortRef.current = controller;
    setAskText('');
    setAsking(false);
    setReply('');
    setTyped('');
    setTyping(true);
    askedRef.current = text;
    /* The user's own words decide the language; the preference covers follow-ups. */
    streamBuddyReply(
      `${text} ${ASK_SHORT_REPLY}`,
      (next) => setReply(regionalSpelling(next, language)),
      controller.signal,
      true
    );
  };

  /* --- travel: hold the bubble closed until he lands -------------------- */
  useEffect(() => {
    if (!anchorId || reduced || inline) return undefined;
    setMoving(true);
    const t = window.setTimeout(() => setMoving(false), TRAVEL_MS);
    return () => window.clearTimeout(t);
  }, [anchorId, reduced, inline]);

  /* --- expression follows the anchor, unless overridden ---------------- */
  useEffect(() => {
    if (overrideRef.current) return;
    setExpression(anchor?.expression || 'neutral');
  }, [anchor]);

  /* --- speak: ask after a pause, and show only the reply --------------- */
  useEffect(() => {
    const page = window.location.pathname;
    const onConversation = isConversationPath(page);
    /* Tucked in the composer he stays silent on a conversation, but an icon visit
       pulls him out, so he speaks for that icon. */
    const quiet =
      covered ||
      (onConversation && !iconPrompt) ||
      isBuddyHiddenPath(page) ||
      !!document.querySelector('[data-testid="loading-overlay"]');
    if (touring) return undefined;
    if (!setting('speak') || !message || quiet || !signedIn) {
      /* A signed-out or hidden page must forget the last line, or login back onto
         that same page sees it as already spoken and stays silent. */
      if (onConversation || !signedIn || isBuddyHiddenPath(page)) {
        askedRef.current = '';
        spokenRef.current = '';
      }
      setReply('');
      setTyped('');
      setTyping(false);
      return undefined;
    }
    /* Hidden in the corner: no completion, and a new line stays unasked so the face click asks once.
       Back on the line he already spoke for, the reply on hand (an Ask answer too) comes back as it is. */
    if (dismissed) {
      if (spokenRef.current === message) askedRef.current = message;
      return undefined;
    }
    /* A hover that is still moving keeps the last reply and does not ask again. */
    if (holding) return undefined;
    /* Leaving an icon falls back to the page line. Keep the reply already shown. */
    const leftIcon = !iconPrompt && askedIconRef.current && page === askedPathRef.current;
    if (leftIcon || askedRef.current === message) return undefined;

    const switchingIcon = Boolean(iconPrompt);
    const controller = new AbortController();
    /* A page line clears the reply now, so Hide Buddy must forget it. An icon line keeps the
       reply until its wait is over, and registers then. */
    if (!switchingIcon) {
      setReply('');
      setTyped('');
      setTyping(false);
      speakAbortRef.current = controller;
    }
    /* Once words arrive, Hide Buddy keeps them for the face click instead of asking again. */
    const started = () => {
      if (speakAbortRef.current === controller) speakAbortRef.current = null;
    };
    const wait = window.setTimeout(() => {
      if (holdingRef.current || controller.signal.aborted) return;
      /* They already left this route. Don't ask for it, and don't pin that reply to the new page. */
      if (!iconPrompt && window.location.pathname !== speechPath) return;
      askAbortRef.current?.abort();
      askedRef.current = message;
      spokenRef.current = message;
      speakAbortRef.current = controller;
      askedPathRef.current = iconPrompt ? page : speechPath;
      askedIconRef.current = Boolean(iconPrompt);
      if (switchingIcon) {
        setReply('');
        setTyped('');
        }
        setTyping(true);
      if (!useAi) {
        started();
        setReply(localAnswer || '');
        return;
      }
      setReply('');
      setTyped('');
      startBuddyConversation();
      Promise.resolve(
        streamBuddyReply(message, (text) => {
          if (controller.signal.aborted) return;
          started();
          setReply(regionalSpelling(text, language));
        }, controller.signal)
      ).then((failed) => {
        started();
        /* The call failed, a 422 included. Fall back to this page's written answer when there is
           one, and otherwise leave the bubble as it was. */
        if (failed && !controller.signal.aborted && localAnswer) setReply(localAnswer);
      });
    }, reduced ? 0 : setting('replyMs'));

    return () => {
      controller.abort();
      window.clearTimeout(wait);
      /* dismiss() has already read it; a cancelled run must not make a later Hide forget a reply. */
      if (speakAbortRef.current === controller) speakAbortRef.current = null;
    };
  }, [
    message,
    localAnswer,
    useAi,
    reduced,
    holding,
    covered,
    signedIn,
    iconPrompt,
    touring,
    speechPath,
    setting,
    language,
    dismissed
  ]);

  /* Reveal the reply one character at a time. No caret. */
  useEffect(() => {
    if (!reply) return undefined;
    if (reduced || typed.length >= reply.length) {
      if (typed !== reply) setTyped(reply);
      setTyping(false);
      return undefined;
    }
    setTyping(true);
    const step = window.setTimeout(() => setTyped(reply.slice(0, typed.length + 1)), 28);
    return () => window.clearTimeout(step);
  }, [reply, typed, reduced]);

  /* --- eyes follow the cursor ------------------------------------------
     Only while the face is neutral. Wink and happy are chevrons — off-centre
     they stop reading as eyes. During a blink the gaze is held, not reset, so
     the eyes do not snap to centre for 120ms. */
  useEffect(() => {
    if (parked || reduced || isMobile) return undefined;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return undefined;

    const state = lookRef.current;
    const clamp = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);

    const blink = () => {
      setBlinking(true);
      window.setTimeout(() => setBlinking(false), 110);
    };

    /* Spring-integrate both eyes toward the gaze. Runs only while something is
       still in motion, so an idle Buddy costs no frames. */
    const tick = () => {
      const l = lookLRef.current;
      const r = lookRRef.current;
      if (!l || !r) {
        state.raf = 0;
        return;
      }
      const nodes = [l, r];
      let alive = false;
      for (let i = 0; i < 2; i += 1) {
        const eye = state.eyes[i];
        const scale = i === 0 ? 1 : LOOK_PARALLAX;
        const tx = state.gx * scale;
        const ty = state.gy * scale;
        eye.vx = (eye.vx + (tx - eye.x) * LOOK_SPRING) * LOOK_DAMP;
        eye.vy = (eye.vy + (ty - eye.y) * LOOK_SPRING) * LOOK_DAMP;
        eye.x += eye.vx;
        eye.y += eye.vy;
        nodes[i].setAttribute('transform', `translate(${eye.x.toFixed(2)} ${eye.y.toFixed(2)})`);
        if (
          Math.abs(tx - eye.x) > 0.03 ||
          Math.abs(ty - eye.y) > 0.03 ||
          Math.abs(eye.vx) > 0.03 ||
          Math.abs(eye.vy) > 0.03
        ) {
          alive = true;
        }
      }
      state.raf = alive ? window.requestAnimationFrame(tick) : 0;
    };

    const kick = () => {
      if (!state.raf) state.raf = window.requestAnimationFrame(tick);
    };

    /* The dead zone is the whole trick: below SACCADE_MIN the eyes simply do
       not move, so they hold and then flick instead of creeping. */
    const setGaze = (nx: number, ny: number, mayBlink: boolean) => {
      const jump = Math.hypot(nx - state.gx, ny - state.gy);
      if (jump < SACCADE_MIN) return;
      state.gx = nx;
      state.gy = ny;
      if (mayBlink && jump > BLINK_JUMP && Math.random() < BLINK_JUMP_CHANCE) blink();
      kick();
    };

    const fromPointer = () => {
      const svg = svgRef.current;
      if (!state.has || !svg || dragRef.current.active) return;
      const typingField = document.activeElement;
      if (typingField && typingField.classList && typingField.classList.contains('chat-mentions-input__input')) {
        return;
      }
      if (expressionRef.current !== 'neutral' || blinkingRef.current) return;
      const rect = svg.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setGaze(
        clamp((state.mx - cx) / LOOK_RANGE) * LOOK_MAX_X,
        clamp((state.my - cy) / LOOK_RANGE) * LOOK_MAX_Y,
        true
      );
    };

    /* One layout read per frame at most, never one per pointer event. */
    const onMove = (e: PointerEvent) => {
      state.mx = e.clientX;
      state.my = e.clientY;
      state.has = true;
      state.lastMove = Date.now();
      if (!state.pending) {
        state.pending = window.requestAnimationFrame(() => {
          state.pending = 0;
          fromPointer();
        });
      }
    };

    const onLeave = () => {
      state.has = false;
    };

    /* Idle glances. Some of these land inside the dead zone and are dropped,
       which is what keeps the rhythm irregular. */
    const scheduleWander = () => {
      state.wander = window.setTimeout(() => {
        const idle = Date.now() - state.lastMove > IDLE_MS;
        if (idle && expressionRef.current === 'neutral' && !blinkingRef.current) {
          setGaze(
            (Math.random() * 2 - 1) * LOOK_MAX_X * WANDER_REACH,
            (Math.random() * 2 - 1) * LOOK_MAX_Y * WANDER_REACH,
            true
          );
        }
        scheduleWander();
      }, WANDER_MIN_MS + Math.random() * (WANDER_MAX_MS - WANDER_MIN_MS));
    };

    state.kick = kick;
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    scheduleWander();
    kick();

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      if (state.raf) window.cancelAnimationFrame(state.raf);
      if (state.pending) window.cancelAnimationFrame(state.pending);
      window.clearTimeout(state.wander);
      state.raf = 0;
      state.pending = 0;
      state.kick = null;
    };
  }, [parked, reduced, isMobile]);

  /* While he is tucked in the field, only the eyes look toward the caret. */
  useEffect(() => {
    if (dismissed || !arriving) return undefined;
    const field = document.querySelector<HTMLTextAreaElement>('.chat-mentions-input__input');
    if (!field) return undefined;
    const aim = () => {
      if (document.activeElement !== field) return;
      const svg = svgRef.current;
      const left = lookLRef.current;
      const right = lookRRef.current;
      if (!svg || !left || !right) return;
      const rect = svg.getBoundingClientRect();
      const dx = caretClientX(field) - (rect.left + rect.width / 2);
      const dy = field.getBoundingClientRect().top - (rect.top + rect.height / 2);
      const clamp = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);
      const nx = clamp(dx / LOOK_RANGE) * LOOK_MAX_X;
      const ny = clamp(dy / LOOK_RANGE) * LOOK_MAX_Y;
      const state = lookRef.current;
      state.gx = nx;
      state.gy = ny;
      state.eyes[0].x = nx;
      state.eyes[0].y = ny;
      state.eyes[1].x = nx * LOOK_PARALLAX;
      state.eyes[1].y = ny * LOOK_PARALLAX;
      left.setAttribute('transform', `translate(${nx.toFixed(2)} ${ny.toFixed(2)})`);
      right.setAttribute(
        'transform',
        `translate(${(nx * LOOK_PARALLAX).toFixed(2)} ${(ny * LOOK_PARALLAX).toFixed(2)})`
      );
    };
    field.addEventListener('input', aim);
    field.addEventListener('keyup', aim);
    field.addEventListener('click', aim);
    document.addEventListener('selectionchange', aim);
    return () => {
      field.removeEventListener('input', aim);
      field.removeEventListener('keyup', aim);
      field.removeEventListener('click', aim);
      document.removeEventListener('selectionchange', aim);
    };
  }, [arriving, dismissed]);

  /* Anything that is not the neutral face recentres the eyes — chevrons pushed
     off-centre stop reading as eyes. Bypasses the dead zone on purpose. */
  useEffect(() => {
    const state = lookRef.current;
    if (expression !== 'neutral') {
      state.gx = 0;
      state.gy = 0;
    }
    if (state.kick) state.kick();
  }, [expression, anchorId]);

  /* --- idle motions: blink while he floats -------------------------------- */
  useEffect(() => {
    if (reduced || parked) return undefined;
    let timer = 0;
    const later = (fn: () => void, ms: number) => {
      timer = window.setTimeout(fn, ms);
    };
    const arm = () => later(play, 1800 + Math.random() * 1600);
    const done = (clear: () => void) => later(() => {
      clear();
      arm();
    }, 0);
    function play() {
      if (busyRef.current) {
        arm();
        return;
      }
        setBlinking(true);
      later(() => done(() => setBlinking(false)), 120);
    }
    arm();
    return () => {
      window.clearTimeout(timer);
          setBlinking(false);
      setBouncing(false);
    };
  }, [reduced, parked]);

  /* --- imperative API --------------------------------------------------- */
  const react = useCallback((next: string, ms = 600) => {
    overrideRef.current = next;
    setExpression(next);
    window.setTimeout(() => {
      overrideRef.current = null;
      setExpression((cur) => (cur === next ? 'neutral' : cur));
    }, ms);
  }, []);

  const celebrate = useCallback(() => {
    react('happy', 1400);
    setBouncing(true);
    window.setTimeout(() => setBouncing(false), 440);
  }, [react]);

  useImperativeHandle(ref, () => ({ react, celebrate, dismiss }), [react, celebrate, dismiss]);

  /* --- declarative triggers on the page -------------------------------- */
  useEffect(() => {
    if (dismissed) return undefined;
    const winkEls = Array.from(document.querySelectorAll('[data-buddy-wink]'));
    const happyEls = Array.from(document.querySelectorAll('[data-buddy-happy]'));
    const onWink = () => react('wink', 600);
    const onHappy = () => celebrate();
    winkEls.forEach((el) => {
      el.addEventListener('mouseenter', onWink);
      el.addEventListener('focus', onWink);
    });
    happyEls.forEach((el) => el.addEventListener('click', onHappy));
    return () => {
      winkEls.forEach((el) => {
        el.removeEventListener('mouseenter', onWink);
        el.removeEventListener('focus', onWink);
      });
      happyEls.forEach((el) => el.removeEventListener('click', onHappy));
    };
  }, [dismissed, react, celebrate]);

  /* Mouse or finger: grab the body and place him. He stays there.
     Re-bind after the loading screen, which unmounts the face. */
  useEffect(() => {
    if (covered) return undefined;
    const node = buddyRef.current;
    if (!node) return undefined;
    const px = (value: string | undefined) => {
      const n = parseFloat(value ?? '');
      if (typeof value === 'string' && value.endsWith('vh')) return (n / 100) * window.innerHeight;
      if (typeof value === 'string' && value.endsWith('vw')) return (n / 100) * window.innerWidth;
      return n || 0;
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element;
      if (arrivingRef.current) return;
      if (typeof e.clientX !== 'number' || typeof e.clientY !== 'number') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (target.closest && target.closest('.klax-buddy__dismiss, .klax-buddy__ask, .klax-buddy__ask-toggle, .klax-buddy__send, .klax-buddy__tour')) return;
      const drag = dragRef.current;
      drag.active = true;
      drag.moved = false;
      drag.fromFace = Boolean(target.closest && target.closest('.klax-buddy__svg'));
      drag.downX = e.clientX;
      drag.downY = e.clientY;
      drag.ox = e.clientX - px(posRef.current['--bx']);
      drag.oy = e.clientY - px(posRef.current['--by']);
      if (setting('move')) setDragging(true);
      setDocked(false);
      try {
        if (node.setPointerCapture) node.setPointerCapture(e.pointerId);
      } catch (err) {
        /* pointer already released */
      }
    };
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || typeof e.clientX !== 'number' || typeof e.clientY !== 'number') return;
      if (!setting('move')) return;
      if (Math.hypot(e.clientX - drag.downX, e.clientY - drag.downY) < 4) return;
      drag.moved = true;
      const half = (sizeRef.current || 102) / 2;
      /* He floats under the sidebar, so a drag stops at its edge instead of hiding him behind it. */
      const x = Math.round(
        Math.min(window.innerWidth - half, Math.max(sidebarRightEdge() + half, e.clientX - drag.ox))
      );
      const y = Math.round(
        Math.min(window.innerHeight - half, Math.max(half, e.clientY - drag.oy))
      );
      drag.x = x;
      drag.y = y;
      drag.placed = true;
      setBubbleSide(bubbleSideFor(x, sizeRef.current || 102));
      setPos((prev) => ({ ...prev, '--bx': `${x}px`, '--by': `${y}px` }));
    };
    const onUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active) return;
      const moved = drag.moved;
      drag.active = false;
      drag.moved = false;
      setDragging(false);
      if (!moved && drag.fromFace) {
        if (dismissed) meet();
        else setSpeechOpen((open) => !open);
      }
      if (node.releasePointerCapture) {
        try {
          node.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* already released */
        }
      }
    };
    node.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      node.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [covered, dismissed, meet, setting]);

  /* While dismissed, park in the corner with the gate open. Leaving arriving
     true after a conversation dock would clip him off-screen. */
  useLayoutEffect(() => {
    if (!parked) return undefined;
    window.clearTimeout(arriveRef.current);
    setArriving(false);
    setDocked(false);
    setApproaching(false);
    /* Pixels in pos, not a calc() pinned at render, so a grab can move him off
       the corner when he covers a control (e.g. pagination). 64px keeps the
       full face on screen, just above the window edge. */
    const pin = () =>
      setPos({
        '--bx': `${window.innerWidth - 64}px`,
        '--by': `${window.innerHeight - 64}px`,
        '--bbubble': '0px',
      });
    pin();
    window.addEventListener('resize', pin);
    return () => window.removeEventListener('resize', pin);
  }, [parked]);

  /* Sit inside the chat canvas, not the sidebar, and stay on screen when the
     window is narrow. A grab holds until the composer is in use. */
  useLayoutEffect(() => {
    if (touring || inline) return undefined;
    let last = '';
    const place = () => {
      if (parkedRef.current || touringRef.current) return;
      const drag = dragRef.current;
      if (drag.active) return;

      const hover = hoverRef.current;
      if (hover) {
        const next = `hover|${hover.x}|${hover.y}`;
        if (next === last) return;
        last = next;
        window.clearTimeout(arriveRef.current);
        setArriving(false);
        setDocked(false);
        setSize(85);
        setApproaching(true);
        setBubbleSide(bubbleSideFor(hover.x, sizeRef.current || 102));
        setPos((prev) => ({ ...prev, '--bx': `${hover.x}px`, '--by': `${hover.y}px` }));
        return;
      }
      setApproaching(false);
      const field = document.querySelector<HTMLTextAreaElement>('.chat-mentions-input__input');
      const card = composerDockCard();
      const typing =
        field &&
        (document.activeElement === field || (field.value && field.value.trim().length > 0));
      const inConversation = isConversationView();
      // Conversation route without a ready composer: stay hidden (covered) — do not float.
      if (isConversationPath(window.location.pathname) && !card) {
        return;
      }
      // Open Buddy docks on a conversation (or while typing). Dismissed Buddy
      // stays parked above — reopening runs this again and returns him to the input.
      if ((typing || inConversation) && card && setting('dock')) {
        const box = card.getBoundingClientRect();
        const dockSize = 56;
        const eyePad = Math.ceil((EYE_RY * dockSize) / 200) + 6;
        const x = Math.round(box.right - 40);
        const y = Math.round(box.top - eyePad);
        const next = `in|${x}|${y}`;
        if (next === last) return;
        last = next;
        setArriving(true);
        setDocked(false);
        setSize(dockSize);
        setPos({
          '--bx': `${x}px`,
          '--by': `${y}px`,
          '--bbubble': '0px',
          '--bcover': `${Math.round(box.top)}px`,
        });
        return;
      }

      window.clearTimeout(arriveRef.current);
      setArriving(false);
      setSize(85);
      if (drag.placed) {
        const half = 51;
        const x = Math.round(
          Math.min((window.innerWidth || 1024) - half, Math.max(sidebarRightEdge() + half, drag.x))
        );
        const y = Math.round(Math.min((window.innerHeight || 768) - half, Math.max(half, drag.y)));
        const next = `held|${x}|${y}`;
        if (next === last) return;
        last = next;
        drag.x = x;
        drag.y = y;
        setDocked(false);
        setBubbleSide(bubbleSideFor(x, 102));
        setPos((prev) => ({
          ...prev,
          '--bx': `${x}px`,
          '--by': `${y}px`,
          '--bbubble': prev['--bbubble'] === '0px' ? '311px' : prev['--bbubble'],
        }));
        return;
      }

      const viewW = window.innerWidth || 1024;
      if (viewW < 768) {
        const x = 64;
        const bubble = Math.max(140, Math.min(240, viewW - 130));
        const next = `narrow|${x}|${bubble}`;
        if (next !== last) {
          last = next;
          setDocked(false);
          setSize(85);
          setBubbleSide(bubbleSideFor(x, 72));
          setPos({ '--bx': `${x}px`, '--by': '18vh', '--bbubble': `${bubble}px` });
        }
        return;
      }

      const content = document.querySelector('[data-testid="app-main-scroll"]');
      const rect = content && content.getBoundingClientRect();
      const contentLeft = rect && rect.width > 0 ? rect.left : 0;
      const right = rect && rect.width > 0 ? rect.right : viewW;
      const sidebarRight = sidebarRightEdge();
      const wide = viewW >= 1024;
      const left = Math.max(sidebarRight, contentLeft, wide && sidebarRight === 0 && contentLeft < 200 ? 280 : 0);
      const half = 51;
      const gap = 20;
      const pad = 16;
      const fitsFullBubble = right - left >= 100 + half + gap + 311 + pad;
      const inset = fitsFullBubble ? 100 : Math.min(72, Math.max(56, (right - left) * 0.16));
      let x = left + inset;
      x = Math.max(left + half + 8, Math.min(x, right - half - pad));
      const room = Math.max(160, right - x - half - gap - pad);
      const next = `${Math.round(x)}|${Math.round(Math.min(311, room))}`;
      if (next === last) return;
      last = next;
      setDocked(false);
      setSize(85);
      const [bx, bubble] = next.split('|');
      setBubbleSide(bubbleSideFor(Number(bx), 80));
      setPos({ '--bx': `${bx}px`, '--by': '30vh', '--bbubble': `${bubble}px` });
    };
    placeRef.current = place;
    place();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    const content = document.querySelector('[data-testid="app-main-scroll"]');
    const sidebar = document.querySelector('[data-sidebar="sidebar"]');
    const field = document.querySelector('.chat-mentions-input__input');
    if (ro && content) ro.observe(content);
    if (ro && sidebar) ro.observe(sidebar);
    if (ro && field) ro.observe(field);
    const onFocusOut = () => window.setTimeout(place, 0);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('focusin', place);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('input', place);
    const onSelect = () => {
      const active = document.activeElement;
      if (active && active.classList && active.classList.contains('chat-mentions-input__input')) place();
    };
    document.addEventListener('selectionchange', onSelect);
    return () => {
      window.clearTimeout(arriveRef.current);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('focusin', place);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('input', place);
      document.removeEventListener('selectionchange', onSelect);
    };
  }, [parked, path, touring, inline, setting]);

  useLayoutEffect(() => {
    if (!touring) {
      setTourBox(null);
      return undefined;
    }
    const placeBesideAgents = () => {
      const step = BUDDY_TOUR[tourStep];
      const target = step ? findBuddyTourTarget(step.target) : null;
      if (!target) {
        setTourBox(null);
        return;
      }
      const box = target.getBoundingClientRect();
      const face = 69;
      const pad = 8;
      const framePad = 4;
      const gap = 12;
      const cardWidth = 298;
      const edge = 16;
      const buddyHalf = (face + 32) / 2;
      const bubble = document.querySelector('.klax-buddy__bubble');
      const cardHeight = bubble instanceof HTMLElement && bubble.offsetHeight > 0 ? bubble.offsetHeight : 160;
      const room = window.innerWidth - (box.right + pad);
      const fitsBeside = room >= gap + face + 20 + cardWidth + edge;
      let x = Math.round(box.right + pad + gap + face / 2);
      let y = Math.round(box.top + box.height / 2);
      let side: 'left' | 'right' = 'right';
      if (step.target === 'composer' && !fitsBeside) {
        side = 'left';
        x = Math.round(Math.min(box.right - face / 2, window.innerWidth - edge - face / 2));
        y = Math.max(Math.round(buddyHalf + edge), Math.round(box.top - gap - cardHeight + buddyHalf));
      }
      const cardTop = y - buddyHalf;
      const lift = Math.max(0, Math.round(cardTop + cardHeight - (window.innerHeight - 32)));
      setTourBox({
        top: box.top - framePad,
        left: box.left - framePad,
        width: box.width + framePad * 2,
        height: box.height + framePad * 2,
      });
      setBubbleSide(side);
      setPos({
        '--bx': `${x}px`,
        '--by': `${y}px`,
        '--bbubble': '0px',
        '--btour-top': `${-lift}px`,
      });
    };
    placeBesideAgents();
    window.addEventListener('resize', placeBesideAgents);
    window.addEventListener('scroll', placeBesideAgents, true);
    return () => {
      window.removeEventListener('resize', placeBesideAgents);
      window.removeEventListener('scroll', placeBesideAgents, true);
    };
  }, [touring, tourStep]);

  useEffect(() => {
    if (dismissed || covered) return undefined;
    const iconButton = (node: EventTarget | null) => {
      if (!(node instanceof Element)) return null;
      if (node.closest('.klax-buddy, .klax-buddy-meet, .klax-buddy-gate')) return null;
      const button = node.closest('button, [role="button"]');
      if (!button) return null;
      const name = button.getAttribute('data-buddy-icon');
      if (!name || !setting('iconTargets').includes(name)) return null;
      return button;
    };
    const approach = (button: Element) => {
      const box = button.getBoundingClientRect();
      const rim = ((sizeRef.current || 102) * 66) / 200;
      const gap = 28;
      const spot = {
        x: Math.round(box.left + box.width / 2),
        y: Math.round(box.bottom + gap + rim),
      };
      hoverRef.current = spot;
      setIconPrompt(promptForIcon(button.getAttribute('aria-label') ?? ''));
      dragRef.current.placed = true;
      dragRef.current.x = spot.x;
      dragRef.current.y = spot.y;
      placeRef.current();
    };
    let dwellTimer = 0;
    let leaveTimer = 0;
    const onOver = (event: PointerEvent) => {
      if (dragRef.current.active || !setting('icons')) return;
      const button = iconButton(event.target);
      if (!button) return;
      holdingRef.current = true;
      setHolding(true);
      window.clearTimeout(leaveTimer);
      window.clearTimeout(dwellTimer);
      dwellTimer = window.setTimeout(() => {
        if (dragRef.current.active) return;
        holdingRef.current = false;
        setHolding(false);
        approach(button);
      }, setting('iconHoverMs'));
    };
    const onOut = (event: PointerEvent) => {
      if (!iconButton(event.target) || iconButton(event.relatedTarget)) return;
      window.clearTimeout(dwellTimer);
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => {
        holdingRef.current = false;
        setHolding(false);
        hoverRef.current = null;
        setIconPrompt(null);
        placeRef.current();
      }, HOVER_LEAVE_MS);
    };
    document.addEventListener('pointerover', onOver);
    document.addEventListener('pointerout', onOut);
    return () => {
      window.clearTimeout(dwellTimer);
      window.clearTimeout(leaveTimer);
      hoverRef.current = null;
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerout', onOut);
    };
  }, [dismissed, covered, setting]);

  /* Cover tracks the route from usePathname (SPA push/replace), not only popstate —
     otherwise login → goto /agents leaves him covered and silent.
     `current` starts unset so each effect run always applies the first sync —
     otherwise agents → /login (logout) resets current to true while React
     covered is still false and skips setCovered, leaving Buddy on the login page. */
  useEffect(() => {
    if (inline) return undefined;
    let current: boolean | undefined;
    let seen = '';
    const sync = () => {
      const page = window.location.pathname;
      const marker = document.querySelector('[data-testid="empty-chat-top-spacer"]') ? 'empty' : 'thread';
      const field = document.querySelector('.chat-mentions-input__input') ? 'field' : 'none';
      const key = `${page}|${marker}|${field}`;
      if (key !== seen) {
        seen = key;
        if (!parkedRef.current) placeRef.current();
      }
      const next =
        hidden ||
        isBuddyHiddenPath(page) ||
        /* An inline Buddy is on the page, so this one steps aside. */
        !!document.querySelector('[data-buddy-inline]') ||
        /* Hide until the conversation composer is ready, then he appears already docked. */
        (isConversationPath(page) && !composerDockCard()) ||
        !!document.querySelector('[data-testid="loading-overlay"]') ||
        /* The phone sidebar sheet slides over the spot he floats in, so step aside while it is open. */
        !!document.querySelector('[data-sidebar="sidebar"][data-mobile="true"]');
      if (next === current) return;
      current = next;
      setCovered(next);
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('popstate', sync);
    return () => {
      mo.disconnect();
      window.removeEventListener('popstate', sync);
    };
  }, [path, hidden, inline]);

  touringRef.current = touring;

  /* Covered or hidden — but the tour must still show when it is opened. */
  if (covered && !touring) return null;
  const hiddenOnDevice = (isMobile && !setting('mobile')) || (isTablet && !setting('tablet'));
  if (hiddenOnDevice && !touring) return null;

  const face = FACES[expression as keyof typeof FACES] || FACES.neutral;
  arrivingRef.current = arriving;
  const speechShown = touring || Boolean(message && (typing || typed) && speechOpen && !dismissed);
  const hideButton = dismissible && !dismissed && !arriving ? (
    <button
      type="button"
      className="klax-buddy__dismiss"
      data-od-id="buddy-dismiss"
      data-side={bubbleSide === 'left' ? 'right' : 'left'}
      onPointerDown={(e) => {
        e.stopPropagation();
        dismiss();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
      }}
      aria-label={labels.dismiss}
    >
      <span aria-hidden="true">
        <Xmark width={12} height={12} strokeWidth={2} />
      </span>
    </button>
  ) : null;
  let shownSize = size;
  if (parked) shownSize = 85;
  if (touring) shownSize = 69;
  if (inline) shownSize = inlineSize;
  const tourCopy = BUDDY_TOUR[tourStep] || BUDDY_TOUR[0];
  const endTour = () => {
    setTouring(false);
    setTourStep(0);
  };
  const advanceTour = () => {
    const next = nextVisibleBuddyTourIndex(tourStep + 1);
    if (next < 0) {
      endTour();
      return;
    }
    setTourStep(next);
  };

  return (
    <div
      className="klax-buddy-gate"
      data-inline={inline ? 'true' : undefined}
      data-buddy-inline={inline && !keepFloating ? '' : undefined}
      data-open={!touring && arriving ? 'true' : 'false'}
      data-touring={touring ? 'true' : undefined}
      style={!touring && arriving ? ({ '--bcover': pos['--bcover'] } as CSSProperties) : undefined}
    >
    {touring ? (
      <>
        <div
          className="klax-buddy__tour-bg"
          style={tourBox ? { clipPath: tourHole(tourBox) } : undefined}
        />
        {tourBox ? (
          <div
            className="klax-buddy__tour-ring"
            style={{
              top: tourBox.top,
              left: tourBox.left,
              width: tourBox.width,
              height: tourBox.height,
            }}
          />
        ) : null}
      </>
    ) : null}
    <div
      ref={buddyRef}
      className="klax-buddy"
      data-od-id="buddy-companion"
      data-dock={touring ? undefined : docked ? 'input' : arriving ? 'going' : undefined}
      data-approach={approaching ? 'true' : 'false'}
      /* Inline sits in the page flow; the mobile hero rule would move him by --bx / --by. */
      data-anchor={inline ? undefined : anchor?.id}
      data-expression={expression}
      data-blink={blinking ? 'true' : 'false'}
      data-bounce={bouncing ? 'true' : 'false'}
      data-dragging={dragging ? 'true' : 'false'}
      data-moving={moving ? 'true' : 'false'}
      data-closing={closing ? 'true' : 'false'}
      data-touring={touring ? 'true' : 'false'}
      data-inline={inline ? 'true' : undefined}
      data-theme={theme}
      role="region"
      aria-label={labels.region}
      style={{ ...pos, '--bsize': `${shownSize}px` } as CSSProperties}
    >
      <div className="klax-buddy__stage" style={{ animationDelay: `${-(startedAt % 4500)}ms` }}>
        <BuddySvg
          svgRef={svgRef}
          lookLRef={lookLRef}
          lookRRef={lookRRef}
          haloId={haloId}
          face={face}
          startedAt={startedAt}
        />
      </div>

      {speechShown ? (
        <BuddySpeech
          touring={touring}
          typing={typing}
          typed={typed}
          asking={asking}
          canAsk={canAsk}
          bubbleSide={bubbleSide}
          theme={theme}
          labels={labels}
          tourCopy={tourCopy}
          addressContext={context}
          onTourClose={endTour}
          onTourOkay={advanceTour}
          askRef={askRef}
          askText={askText}
          onAskTextChange={setAskText}
          onAskSend={sendAsk}
          onAskOpen={() => setAsking(true)}
          onAskClose={() => setAsking(false)}
          dismissButton={hideButton}
        />
      ) : null}

      {speechShown ? null : hideButton}
      </div>
    </div>
  );
});

/** Ask needs the member's plan. Only a Buddy with Ask on loads it, because useMyPlan throws
    before the app settings exist — and an inline Buddy can sit on that loading screen. */
const BuddyWithPlan = forwardRef<BuddyHandle, BuddyBodyProps>(function BuddyWithPlan(props, ref) {
  const { hasPlan } = useMyPlan();
  return <BuddyBody ref={ref} {...props} hasPlan={hasPlan} />;
});

/** The floating Buddy speaks, so he reads the user's form from the preference. An inline one
    stays silent and can sit outside the app's query and router providers, so it doesn't. */
const BuddyWithPreferences = forwardRef<BuddyHandle, BuddyBodyProps & { withPlan: boolean }>(
  function BuddyWithPreferences({ withPlan, ...props }, ref) {
    const addressForm = useAddressForm();
    const View = withPlan ? BuddyWithPlan : BuddyBody;
    return <View ref={ref} {...props} addressForm={addressForm} />;
  }
);

/**
 * One Buddy for every place. Without `inline` he floats over the app; with it he sits in the
 * page flow. What he does comes from BUDDY_CONFIG, then BUDDY_INLINE_CONFIG for inline, then
 * `config` on this use, e.g. `<Buddy inline config={{ speak: true }} />`.
 */
const Buddy = forwardRef<BuddyHandle, BuddyProps>(function Buddy({ inline = false, config, ...props }, ref) {
  const overrides = { ...(inline ? BUDDY_INLINE_CONFIG : {}), ...config };
  if (!readSetting(overrides, 'on')) return null;
  const withPlan = readSetting(overrides, 'ask');
  if (!inline) {
    return (
      <BuddyWithPreferences
        ref={ref}
        withPlan={withPlan}
        dismissible
        {...props}
        inline={false}
        overrides={overrides}
      />
    );
  }
  const View = withPlan ? BuddyWithPlan : BuddyBody;
  return <View ref={ref} dismissible={false} {...props} inline overrides={overrides} />;
});

export default Buddy;
